import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Avatar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PushPinIcon from '@mui/icons-material/PushPin';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useFamily } from '../contexts/FamilyContext';

const FamilyFeed = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentFamily } = useFamily();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const fetchPosts = useCallback(async () => {
    if (!currentFamily) return;
    try {
      const response = await api.get(`/family-posts/?family_id=${currentFamily.id}`);
      const postsData = response.data.results || response.data;
      setPosts(postsData);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [currentFamily]);

  useEffect(() => {
    if (currentFamily) {
      fetchPosts();
    } else {
      setLoading(false);
    }
  }, [currentFamily, fetchPosts]);

  const handleCreate = async () => {
    if (!currentFamily) {
      alert('Please select a family first');
      return;
    }
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('content', formData.content);
      formDataToSend.append('family_id', currentFamily.id);
      if (imageFile) {
        formDataToSend.append('image', imageFile);
      }

      const response = await api.post('/family-posts/', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setPosts([response.data, ...posts]);
      handleCloseDialog();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.detail || 
                          error.response?.data?.non_field_errors?.[0] ||
                          error.message || 
                          'Failed to create post';
      alert(errorMessage);
    }
  };

  const handleUpdate = async () => {
    if (!editingPost) return;
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('content', formData.content);
      if (imageFile) {
        formDataToSend.append('image', imageFile);
      } else if (imagePreview === null) {
        // If imagePreview is null, user wants to remove the image
        formDataToSend.append('image', '');
      }

      const response = await api.patch(`/family-posts/${editingPost.id}/`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setPosts(posts.map(p => p.id === editingPost.id ? response.data : p));
      handleCloseDialog();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.detail || 
                          error.response?.data?.non_field_errors?.[0] ||
                          error.message || 
                          'Failed to update post';
      alert(errorMessage);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
    });
    setImageFile(null);
    setImagePreview(post.image_url || null);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingPost(null);
    setFormData({ title: '', content: '' });
    setImageFile(null);
    setImagePreview(null);
  };

  const handleTogglePin = async (postId) => {
    try {
      const response = await api.post(`/family-posts/${postId}/toggle_pin/`);
      setPosts(posts.map(p => p.id === postId ? response.data : p));
    } catch (error) {
      alert('Failed to toggle pin');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await api.delete(`/family-posts/${postId}/`);
      setPosts(posts.filter(p => p.id !== postId));
    } catch (error) {
      alert('Failed to delete post');
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!currentFamily) {
    return (
      <Container>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              No Family Selected
            </Typography>
            <Typography>
              Please create or join a family to view the feed.
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => navigate('/families')}
            >
              Go to Families
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  const userMember = currentFamily.members?.find((m) => m.user_id === user?.uid);
  const isAdmin = userMember && ['owner', 'admin'].includes(userMember.role);
  
  const canEditPost = (post) => {
    return post.user_id === user?.uid || isAdmin;
  };

  // Separate pinned and regular posts
  const pinnedPosts = posts.filter(p => p.is_pinned);
  const regularPosts = posts.filter(p => !p.is_pinned);

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 2, sm: 4 }, mb: 4, px: { xs: 1, sm: 2 } }}>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={2}
        mb={3}
      >
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
          {currentFamily.name} Feed
        </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
            fullWidth={{ xs: true, sm: false }}
          >
            New Post
          </Button>
      </Box>

      {posts.length === 0 ? (
        <Card>
          <CardContent>
            <Typography align="center" color="text.secondary">
              No posts yet. {isAdmin && 'Create the first post to share with your family!'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Pinned Posts */}
          {pinnedPosts.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                📌 Pinned
              </Typography>
              {pinnedPosts.map((post) => (
                <Card key={post.id} sx={{ mb: 2, border: '2px solid', borderColor: '#7A9A8C' }}>
                  {post.image_url && (
                    <CardMedia
                      component="img"
                      height={{ xs: 200, sm: 300 }}
                      image={post.image_url}
                      alt={post.title}
                      sx={{ objectFit: 'cover' }}
                    />
                  )}
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Box 
                      display="flex" 
                      justifyContent="space-between" 
                      alignItems="start" 
                      mb={1}
                      flexDirection={{ xs: 'column', sm: 'row' }}
                      gap={1}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }}>
                          {post.user_name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {post.user_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            {new Date(post.created_at).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                      <Box display="flex" gap={0.5}>
                        {isAdmin && (
                            <IconButton
                              size="small"
                              onClick={() => handleTogglePin(post.id)}
                              title="Unpin"
                            >
                              <PushPinIcon fontSize="small" />
                          </IconButton>
                        )}
                        {canEditPost(post) && (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(post)}
                              title="Edit"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(post.id)}
                              color="error"
                              title="Delete"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </Box>
                    <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                      {post.title}
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                      {post.content}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {/* Regular Posts */}
          {regularPosts.length > 0 && (
            <Box>
              {pinnedPosts.length > 0 && (
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Recent Posts
                </Typography>
              )}
              {regularPosts.map((post) => (
                <Card key={post.id} sx={{ mb: 2 }}>
                  {post.image_url && (
                    <CardMedia
                      component="img"
                      height={{ xs: 200, sm: 300 }}
                      image={post.image_url}
                      alt={post.title}
                      sx={{ objectFit: 'cover' }}
                    />
                  )}
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {post.user_name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">{post.user_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(post.created_at).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                      <Box>
                        {isAdmin && (
                            <IconButton
                              size="small"
                              onClick={() => handleTogglePin(post.id)}
                              title="Pin"
                            >
                              <PushPinIcon fontSize="small" color="disabled" />
                          </IconButton>
                        )}
                        {canEditPost(post) && (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(post)}
                              title="Edit"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(post.id)}
                              color="error"
                              title="Delete"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {post.title}
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {post.content}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </>
      )}

      <Dialog 
        open={open} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { m: { xs: 1, sm: 2 }, maxHeight: '90vh' }
        }}
      >
        <DialogTitle>{editingPost ? 'Edit Post' : 'Create Family Post'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Title"
            margin="normal"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            autoFocus
          />
          <TextField
            fullWidth
            label="Content"
            margin="normal"
            multiline
            rows={6}
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            required
          />
          <Box sx={{ mt: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="image-upload"
              type="file"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setImageFile(file);
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setImagePreview(reader.result);
                  };
                  reader.readAsDataURL(file);
                }
              }}
            />
            <label htmlFor="image-upload">
              <Button variant="outlined" component="span" fullWidth sx={{ mb: 1 }}>
                {imageFile ? 'Change Image' : (imagePreview ? 'Change Image' : 'Upload Image (optional)')}
              </Button>
            </label>
            {imagePreview && (
              <Box sx={{ mt: 2, position: 'relative' }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    width: '100%',
                    maxHeight: '200px',
                    objectFit: 'contain',
                    borderRadius: '4px',
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    setImageFile(null);
                    setImagePreview(null);
                  }}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'rgba(255,255,255,0.8)',
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={editingPost ? handleUpdate : handleCreate} 
            variant="contained" 
            disabled={!formData.title || !formData.content}
          >
            {editingPost ? 'Update' : 'Post'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FamilyFeed;
